/**
 * PainelDeNavegacao — a navegação do produto como DADO, no Design Studio (Onda 2).
 *
 * Defeito visto em campo (Picky V10): o design tinha a barra Point · Farejar · Início · Criar ·
 * Perfil; a web e o mobile entregaram cada um uma barra diferente, porque a navegação não era
 * dado em lugar nenhum. Aqui o dono vê o mapa que a plataforma tirou das telas e decide o que
 * ela não decidiu:
 *
 *   1. a navegação principal de cada plataforma (confirmada / proposta / indefinida);
 *   2. as ações sem destino decidido — uma a uma, ou com sugestão da IA confirmada em lote.
 *
 * Só o que está decidido entra no contrato do Gate B e no prompt das telas.
 */
import { useState } from 'react';
import { C } from '@/theme/palette';

const TIPO_PT = { tela: 'outra tela', estado: 'muda algo nesta tela', voltar: 'volta', externo: 'sai do produto' };
const STATUS_DA_CASCA = { confirmada: 'confirmada', proposta: 'proposta — falta confirmar', indefinida: 'não definida' };
const PLATAFORMA_PT = { mobile: 'Celular', web: 'Web' };
const caixa = { border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 12.5 };
const botao = (ativo = true) => ({ cursor: ativo ? 'pointer' : 'default', background: 'transparent',
  color: C.accent, border: `1px solid ${C.accent}66`, borderRadius: 7, padding: '5px 11px',
  fontSize: 12, fontWeight: 600, opacity: ativo ? 1 : 0.5 });

/** Contagem das transições por situação. */
export function resumoDoMapa(mapa) {
  const tr = mapa?.transicoes || [];
  const conta = (s) => tr.filter((t) => t.status === s).length;
  return {
    total: tr.length,
    decididas: conta('confirmada') + conta('resolvida'),
    aConfirmar: conta('a_confirmar'),
    pendentes: conta('pendente'),
    semEntrada: (mapa?.analise?.telas_sem_entrada || []).length,
  };
}

function CascaDaPlataforma({ plataforma, casca, telas, onConfirmar, ocupado }) {
  const [itens, setItens] = useState(() => (casca?.itens || []).map((i) => ({ rotulo: i.rotulo || '', tela: i.tela || '' })));
  const [inicial, setInicial] = useState(casca?.tela_inicial || '');
  const completos = itens.filter((i) => i.rotulo.trim() && i.tela);
  const podeConfirmar = !ocupado && completos.length >= 2 && completos.length === itens.length;
  const mudar = (idx, campo, valor) => setItens((ant) => ant.map((i, n) => (n === idx ? { ...i, [campo]: valor } : i)));
  return (
    <div data-testid={`casca-${plataforma}`} style={{ ...caixa, flex: 1, minWidth: 300 }}>
      <strong style={{ color: C.text }}>{PLATAFORMA_PT[plataforma]}</strong>{' '}
      <span data-testid={`casca-status-${plataforma}`}
        style={{ color: casca?.status === 'confirmada' ? C.accent : C.warn }}>
        — {STATUS_DA_CASCA[casca?.status] || casca?.status}
      </span>
      {itens.map((i, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={idx} style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <input aria-label={`Rótulo do item ${idx + 1} (${plataforma})`} value={i.rotulo}
            onChange={(e) => mudar(idx, 'rotulo', e.target.value)}
            style={{ width: 110, background: C.surface, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, padding: '4px 6px', fontSize: 12 }} />
          <select aria-label={`Tela do item ${idx + 1} (${plataforma})`} value={i.tela}
            onChange={(e) => mudar(idx, 'tela', e.target.value)}
            style={{ flex: 1, background: C.surface, color: C.text, border: `1px solid ${i.tela ? C.border : C.warn}`, borderRadius: 6, fontSize: 12 }}>
            <option value="">— escolha a tela —</option>
            {telas.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
          </select>
          <button type="button" aria-label={`Remover o item ${idx + 1} (${plataforma})`}
            onClick={() => setItens((ant) => ant.filter((_x, n) => n !== idx))} style={botao()}>×</button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
        <button type="button" onClick={() => setItens((ant) => [...ant, { rotulo: '', tela: '' }])} style={botao()}>
          + item
        </button>
        <label style={{ color: C.dim }}>
          Tela inicial{' '}
          <select aria-label={`Tela inicial (${plataforma})`} value={inicial} onChange={(e) => setInicial(e.target.value)}
            style={{ background: C.surface, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12 }}>
            <option value="">—</option>
            {telas.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
          </select>
        </label>
        <button type="button" disabled={!podeConfirmar}
          aria-label={`Confirmar a navegação principal (${plataforma})`}
          onClick={() => onConfirmar({ plataforma, tipo: casca?.tipo || (plataforma === 'web' ? 'sidebar' : 'tabs'),
            itens: completos, tela_inicial: inicial || null })}
          style={{ ...botao(podeConfirmar), marginLeft: 'auto' }}>
          ✓ Confirmar
        </button>
      </div>
      {(casca?.avisos || []).length > 0 && (
        <ul style={{ margin: '8px 0 0', paddingLeft: 18, color: C.warn }}>
          {casca.avisos.map((a) => <li key={a}>{a}</li>)}
        </ul>
      )}
    </div>
  );
}

function LinhaPendente({ tr, telas, sugestao, onDecidir, ocupado }) {
  const [tipo, setTipo] = useState(sugestao?.tipo || 'tela');
  const [para, setPara] = useState(sugestao?.paraTela || tr.paraTela || '');
  const pode = !ocupado && (tipo !== 'tela' || Boolean(para));
  return (
    <li data-testid="acao-pendente" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '5px 0', borderTop: `1px solid ${C.border}` }}>
      <span style={{ flex: 1, minWidth: 260, color: C.text }}>
        <strong>{tr.deTela}</strong> · {tr.rotulo}{' '}
        <span style={{ color: C.muted }}>— anotado: “{tr.destino}”</span>
        {sugestao && <span style={{ color: C.accent }}> · sugestão da IA</span>}
        {tr.status === 'a_confirmar' && !sugestao && <span style={{ color: C.warn }}> · palpite da regra</span>}
      </span>
      <select aria-label={`Tipo do destino de ${tr.rotulo} (${tr.deTela})`} value={tipo} onChange={(e) => setTipo(e.target.value)}
        style={{ background: C.surface, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12 }}>
        {Object.entries(TIPO_PT).map(([id, nome]) => <option key={id} value={id}>{nome}</option>)}
      </select>
      {tipo === 'tela' && (
        <select aria-label={`Tela de destino de ${tr.rotulo} (${tr.deTela})`} value={para} onChange={(e) => setPara(e.target.value)}
          style={{ maxWidth: 240, background: C.surface, color: C.text, border: `1px solid ${para ? C.border : C.warn}`, borderRadius: 6, fontSize: 12 }}>
          <option value="">— escolha a tela —</option>
          {telas.filter((t) => t.name !== tr.deTela).map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
        </select>
      )}
      <button type="button" disabled={!pode} aria-label={`Confirmar o destino de ${tr.rotulo} (${tr.deTela})`}
        onClick={() => onDecidir({ de_tela: tr.deTela, rotulo: tr.rotulo, tipo, para_tela: tipo === 'tela' ? para : null })}
        style={botao(pode)}>✓</button>
    </li>
  );
}

/**
 * @param {{ mapa: object|null|undefined, sugestoes?: object[], onDecidir: Function,
 *           onConfirmarSugestoes: Function, onPedirSugestoes: Function, onConfirmarCasca: Function,
 *           ocupado?: boolean, sugerindo?: boolean, erro?: string|null, avisos?: string[] }} props
 */
export function PainelDeNavegacao({ mapa, sugestoes = [], onDecidir, onConfirmarSugestoes,
  onPedirSugestoes, onConfirmarCasca, ocupado = false, sugerindo = false, erro = null, avisos = [] }) {
  const [aberto, setAberto] = useState(false);
  if (!mapa || !(mapa.telas || []).length) return null;
  const r = resumoDoMapa(mapa);
  const abertas = (mapa.transicoes || []).filter((t) => t.status === 'pendente' || t.status === 'a_confirmar');
  const sugestaoDe = (t) => sugestoes.find((s) => s.deTela === t.deTela && s.rotulo === t.rotulo);
  const comSugestao = abertas.filter(sugestaoDe);
  const semEntrada = mapa.analise?.telas_sem_entrada || [];
  return (
    <section data-testid="painel-navegacao" style={{ margin: '0 0 14px' }}>
      <h4 style={{ margin: '0 0 6px', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.1em', color: C.accent }}>
        Navegação do produto
      </h4>
      <div data-testid="resumo-navegacao" style={{ color: C.dim, fontSize: 12.5, marginBottom: 8 }}>
        {r.total.toLocaleString('pt-BR')} ação(ões) nas telas ·{' '}
        <strong style={{ color: C.text }}>{r.decididas.toLocaleString('pt-BR')} com destino decidido</strong> ·{' '}
        <span style={{ color: r.aConfirmar + r.pendentes ? C.warn : C.dim }}>
          {(r.aConfirmar + r.pendentes).toLocaleString('pt-BR')} sem decisão
        </span>{' '}
        · {r.semEntrada.toLocaleString('pt-BR')} tela(s) que nada alcança.
        {' '}Só o que está decidido vai ao contrato e ao codegen.
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {['mobile', 'web'].map((p) => (
          <CascaDaPlataforma key={`${p}-${mapa.casca?.[p]?.status}-${(mapa.casca?.[p]?.itens || []).length}`}
            plataforma={p} casca={mapa.casca?.[p]} telas={mapa.telas} onConfirmar={onConfirmarCasca} ocupado={ocupado} />
        ))}
      </div>
      {semEntrada.length > 0 && (
        <div data-testid="telas-sem-entrada" style={{ ...caixa, marginTop: 10, color: C.warn }}>
          Telas que nenhuma ação decidida, barra ou tela inicial alcança: {semEntrada.join('; ')}.
        </div>
      )}
      {(avisos || []).filter(Boolean).map((a) => (
        <div key={a} role="alert" style={{ marginTop: 8, color: C.warn, fontSize: 12.5 }}>{a}</div>
      ))}
      {erro && <div role="alert" style={{ marginTop: 8, color: C.digital, fontSize: 12.5 }}>{String(erro)}</div>}
      {abertas.length > 0 && (
        <div style={{ ...caixa, marginTop: 10 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <strong style={{ color: C.text, flex: 1 }}>
              {abertas.length.toLocaleString('pt-BR')} ação(ões) sem destino decidido
            </strong>
            <button type="button" onClick={onPedirSugestoes} disabled={sugerindo || ocupado} style={botao(!sugerindo && !ocupado)}>
              {sugerindo ? 'Pedindo sugestões…' : '✨ Pedir sugestão à IA'}
            </button>
            {comSugestao.length > 0 && (
              <button type="button" disabled={ocupado}
                aria-label={`Confirmar as ${comSugestao.length.toLocaleString('pt-BR')} sugestões da IA`}
                onClick={() => onConfirmarSugestoes(comSugestao.map((t) => {
                  const s = sugestaoDe(t);
                  return { de_tela: t.deTela, rotulo: t.rotulo, tipo: s.tipo, para_tela: s.paraTela || null };
                }))}
                style={botao(!ocupado)}>
                ✓ Confirmar as {comSugestao.length.toLocaleString('pt-BR')} sugestões
              </button>
            )}
            <button type="button" onClick={() => setAberto((v) => !v)} style={botao()}>
              {aberto ? 'Recolher' : 'Revisar uma a uma'}
            </button>
          </div>
          {aberto && (
            <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0' }}>
              {abertas.map((t) => (
                <LinhaPendente key={`${t.de}|${t.rotulo}|${sugestaoDe(t)?.tipo || ''}|${sugestaoDe(t)?.paraTela || ''}`}
                  tr={t} telas={mapa.telas} sugestao={sugestaoDe(t)} onDecidir={onDecidir} ocupado={ocupado} />
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
